/**
 * Centralized configuration for Solana Tracker API
 * This file manages all API endpoints, rate limiting, and error handling
 */

import { env } from '../config/env';

export const SOLANA_TRACKER_CONFIG = {
    // API Configuration
    BASE_URL: 'https://data.solanatracker.io',
    WS_URL: env.SOLANA_TRACKER_WS_URL || 'wss://ws.solanatracker.io',
    API_KEY: env.SOLANA_TRACKER_API_KEY || '',

    // Rate Limiting
    RATE_LIMIT: {
        REQUESTS_PER_SECOND: 10,
        BURST_LIMIT: 50,
        RETRY_ATTEMPTS: 3,
        RETRY_DELAY: 1000, // 1 second
        BACKOFF_MULTIPLIER: 2
    },

    // Cache Configuration
    CACHE: {
        TOKEN_DATA_TTL: 30000, // 30 seconds
        WALLET_DATA_TTL: 60000, // 1 minute
        PNL_DATA_TTL: 30000, // 30 seconds
        CHART_DATA_TTL: 60000, // 1 minute
        MAX_CACHE_SIZE: 1000 // Maximum number of cached entries
    },

    // Standard Headers
    HEADERS: {
        'x-api-key': env.SOLANA_TRACKER_API_KEY || '',
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    },

    // Timeout Configuration
    TIMEOUTS: {
        DEFAULT: 10000, // 10 seconds
        CHART_DATA: 15000, // 15 seconds for chart data
        BATCH_REQUESTS: 30000 // 30 seconds for batch operations
    },

    // Error Handling
    ERROR_HANDLING: {
        RETRY_ON_ERRORS: [429, 500, 502, 503, 504],
        FALLBACK_ON_ERRORS: [401, 403, 404],
        LOG_ERRORS: true
    }
};

/**
 * Rate limiter class to manage API request frequency
 */
class RateLimiter {
    private requests: number[] = [];
    private readonly maxRequests: number;
    private readonly timeWindow: number = 1000; // 1 second

    constructor(maxRequests: number = SOLANA_TRACKER_CONFIG.RATE_LIMIT.REQUESTS_PER_SECOND) {
        this.maxRequests = maxRequests;
    }

    async waitForSlot(): Promise<void> {
        const now = Date.now();

        // Remove requests older than 1 second
        this.requests = this.requests.filter(time => now - time < this.timeWindow);

        if (this.requests.length >= this.maxRequests) {
            // Wait until the oldest request is more than 1 second old
            const oldestRequest = this.requests[0];
            const waitTime = this.timeWindow - (now - oldestRequest);

            if (waitTime > 0) {
                await new Promise(resolve => setTimeout(resolve, waitTime));
                return this.waitForSlot(); // Recursive call to check again
            }
        }

        this.requests.push(now);
    }
}

/**
 * Global rate limiter instance
 */
export const rateLimiter = new RateLimiter();

/**
 * Validates API key configuration
 */
export function validateApiKey(): boolean {
    if (!SOLANA_TRACKER_CONFIG.API_KEY) {
        console.error('❌ VITE_SOLANA_TRACKER_API_KEY is not configured');
        console.warn('🔧 Please add VITE_SOLANA_TRACKER_API_KEY=your_api_key to your .env file');
        return false;
    }
    return true;
}

/**
 * Creates standardized fetch options
 */
export function createFetchOptions(overrides?: RequestInit): RequestInit {
    return {
        method: 'GET',
        signal: AbortSignal.timeout(SOLANA_TRACKER_CONFIG.TIMEOUTS.DEFAULT),
        ...overrides,
        headers: {
            ...SOLANA_TRACKER_CONFIG.HEADERS,
            ...overrides?.headers
        }
    };
}

/**
 * Standard error handling for API responses
 */
export function handleApiError(error: any, operation: string): never {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';

    if (SOLANA_TRACKER_CONFIG.ERROR_HANDLING.LOG_ERRORS) {
        console.error(`❌ Solana Tracker API error in ${operation}:`, errorMessage);
    }

    // Enhance error message with context
    const enhancedError = new Error(`Solana Tracker API error in ${operation}: ${errorMessage}`);
    enhancedError.cause = error;

    throw enhancedError;
}

/**
 * Determines if an error should trigger a retry
 */
export function shouldRetry(error: any, attempt: number): boolean {
    if (attempt >= SOLANA_TRACKER_CONFIG.RATE_LIMIT.RETRY_ATTEMPTS) {
        return false;
    }

    // Check if error is a network error or retryable status code
    if (error instanceof TypeError && error.message.includes('fetch')) {
        return true; // Network error
    }

    if (error.message) {
        for (const code of SOLANA_TRACKER_CONFIG.ERROR_HANDLING.RETRY_ON_ERRORS) {
            if (error.message.includes(code.toString())) {
                return true;
            }
        }
    }

    return false;
}

/**
 * Calculates retry delay with exponential backoff
 */
export function getRetryDelay(attempt: number): number {
    return SOLANA_TRACKER_CONFIG.RATE_LIMIT.RETRY_DELAY *
        Math.pow(SOLANA_TRACKER_CONFIG.RATE_LIMIT.BACKOFF_MULTIPLIER, attempt);
}

/**
 * Cache class for managing API response caching
 */
export class ApiCache {
    private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();

    set(key: string, data: any, ttl: number = SOLANA_TRACKER_CONFIG.CACHE.TOKEN_DATA_TTL): void {
        // Prevent cache from growing too large
        if (this.cache.size >= SOLANA_TRACKER_CONFIG.CACHE.MAX_CACHE_SIZE) {
            this.cleanup();
        }

        this.cache.set(key, {
            data,
            timestamp: Date.now(),
            ttl
        });
    }

    get(key: string): any | null {
        const entry = this.cache.get(key);

        if (!entry) {
            return null;
        }

        const now = Date.now();
        if (now - entry.timestamp > entry.ttl) {
            this.cache.delete(key);
            return null;
        }

        return entry.data;
    }

    delete(key: string): void {
        this.cache.delete(key);
    }

    clear(): void {
        this.cache.clear();
    }

    cleanup(): void {
        const now = Date.now();
        const keysToDelete: string[] = [];

        for (const [key, entry] of this.cache.entries()) {
            if (now - entry.timestamp > entry.ttl) {
                keysToDelete.push(key);
            }
        }

        for (const key of keysToDelete) {
            this.cache.delete(key);
        }

        // If cache is still too large, remove oldest entries
        if (this.cache.size >= SOLANA_TRACKER_CONFIG.CACHE.MAX_CACHE_SIZE) {
            const entries = Array.from(this.cache.entries());
            entries.sort((a, b) => a[1].timestamp - b[1].timestamp);

            const toRemove = entries.slice(0, Math.floor(SOLANA_TRACKER_CONFIG.CACHE.MAX_CACHE_SIZE * 0.1));
            for (const [key] of toRemove) {
                this.cache.delete(key);
            }
        }
    }

    getStats(): { size: number; hitRate: number } {
        return {
            size: this.cache.size,
            hitRate: 0 // TODO: Implement hit rate tracking
        };
    }
}

/**
 * Global cache instance
 */
export const apiCache = new ApiCache(); 